"use client";
import React from "react";
import { motion } from "motion/react";
import { ArrowLeft, FileText, Scale, Shield, AlertCircle } from "lucide-react";
import Link from "next/link";
import { GlassCard } from "@/src/components/GlassCard";
import { useTranslation } from "react-i18next";

export const TermsOfServicePage: React.FC = () => {
  const { t } = useTranslation("common");
  const sections = [
    {
      title: "1. Acceptance of Terms",
      icon: FileText,
      content: `
        <p>By accessing and using the Walnut Packaging Factory website and services ("Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this Service.</p>
        <p>These Terms of Service ("Terms") govern your use of our website, die-cut design studio, quote request system, and all related services provided by Walnut Packaging Factory.</p>
      `,
    },
    {
      title: "2. Use License",
      icon: Scale,
      content: `
        <p>Permission is granted to temporarily access and use the materials on Walnut Packaging Factory's website for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:</p>
        <ul>
          <li>Modify or copy the materials</li>
          <li>Use the materials for any commercial purpose or for any public display</li>
          <li>Attempt to reverse engineer any software contained on Walnut Packaging Factory's website</li>
          <li>Remove any copyright or other proprietary notations from the materials</li>
          <li>Transfer the materials to another person or "mirror" the materials on any other server</li>
        </ul>
        <p>This license shall automatically terminate if you violate any of these restrictions and may be terminated by Walnut Packaging Factory at any time.</p>
      `,
    },
    {
      title: "3. Design Studio and Intellectual Property",
      icon: Shield,
      content: `
        <h4>Your Designs</h4>
        <p>When you create designs using our die-cut design studio:</p>
        <ul>
          <li>You retain ownership of your original artwork and content</li>
          <li>You grant Walnut Packaging Factory a license to use your designs for the purpose of manufacturing your ordered products</li>
          <li>You represent that you have the right to use all elements in your design (images, logos, text, etc.)</li>
          <li>You agree not to upload content that infringes on third-party intellectual property rights</li>
        </ul>
        <h4>Our Templates</h4>
        <p>All die-cut templates, design tools, and pre-made elements provided by Walnut Packaging Factory are:</p>
        <ul>
          <li>Licensed for use only through our platform</li>
          <li>Protected by copyright and intellectual property laws</li>
          <li>Not to be reproduced, distributed, or used outside our platform without written permission</li>
        </ul>
      `,
    },
    {
      title: "4. User Accounts",
      icon: Shield,
      content: `
        <p>When you create an account with us, you agree to:</p>
        <ul>
          <li>Provide accurate, current, and complete information</li>
          <li>Maintain and promptly update your account information</li>
          <li>Maintain the security of your password and accept all risks of unauthorized access</li>
          <li>Immediately notify us of any unauthorized use of your account</li>
        </ul>
        <p>We reserve the right to suspend or terminate your account if any information provided proves to be inaccurate, not current, or incomplete.</p>
      `,
    },
    {
      title: "5. Quotes and Orders",
      icon: FileText,
      content: `
        <h4>Quote Requests</h4>
        <p>When you request a quote through our system:</p>
        <ul>
          <li>Quotes are estimates and subject to change based on final specifications</li>
          <li>Quotes are valid for 30 days from the date of issue</li>
          <li>Final pricing will be confirmed before production begins</li>
          <li>Minimum order quantities may apply</li>
        </ul>
        <h4>Orders</h4>
        <p>By placing an order, you agree that:</p>
        <ul>
          <li>You are legally capable of entering into binding contracts</li>
          <li>All order details provided are accurate and complete</li>
          <li>You authorize the payment method provided</li>
          <li>You understand and accept our production timelines</li>
        </ul>
      `,
    },
    {
      title: "6. Pricing and Payment",
      icon: Scale,
      content: `
        <p>All prices are listed in USD unless otherwise specified. We reserve the right to:</p>
        <ul>
          <li>Change prices at any time without notice</li>
          <li>Correct pricing errors, even after orders have been placed</li>
          <li>Refuse or cancel orders with pricing errors</li>
        </ul>
        <p>Payment terms:</p>
        <ul>
          <li>Payment is due before production begins unless credit terms have been established</li>
          <li>We accept major credit cards, ACH transfers, and wire transfers</li>
          <li>Late payments may incur additional fees</li>
          <li>Orders may be held until payment is received in full</li>
        </ul>
      `,
    },
    {
      title: "7. Production and Delivery",
      icon: AlertCircle,
      content: `
        <h4>Production Times</h4>
        <p>Estimated production times are provided as guidelines and are not guaranteed. Delays may occur due to:</p>
        <ul>
          <li>Design approval processes</li>
          <li>Material availability</li>
          <li>Production complications</li>
          <li>Force majeure events</li>
        </ul>
        <h4>Delivery</h4>
        <p>Shipping and delivery terms:</p>
        <ul>
          <li>Risk of loss passes to you upon delivery to the carrier</li>
          <li>Delivery dates are estimates and not guaranteed</li>
          <li>You are responsible for providing accurate shipping information</li>
          <li>Additional charges may apply for expedited shipping</li>
        </ul>
      `,
    },
    {
      title: "8. Quality and Returns",
      icon: Shield,
      content: `
        <h4>Quality Assurance</h4>
        <p>We strive for excellence, but due to the nature of custom manufacturing:</p>
        <ul>
          <li>Minor variations in color, size, and printing may occur</li>
          <li>Color matching is approximate, not exact</li>
          <li>A variance of +/- 10% in quantity is considered acceptable</li>
        </ul>
        <h4>Returns and Refunds</h4>
        <p>Custom-manufactured products are generally not returnable. However, we will:</p>
        <ul>
          <li>Replace products with manufacturing defects</li>
          <li>Correct errors caused by our team</li>
          <li>Work with you to resolve quality concerns</li>
        </ul>
        <p>Claims must be made within 7 days of delivery with photographic evidence.</p>
      `,
    },
    {
      title: "9. Limitation of Liability",
      icon: AlertCircle,
      content: `
        <p>Walnut Packaging Factory shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation:</p>
        <ul>
          <li>Loss of profits or revenues</li>
          <li>Loss of data or use</li>
          <li>Business interruption</li>
          <li>Cost of substitute goods or services</li>
        </ul>
        <p>Our total liability for any claim arising out of or relating to these Terms or our Service shall not exceed the amount paid by you for the specific product or service giving rise to the claim.</p>
      `,
    },
    {
      title: "10. Indemnification",
      icon: Scale,
      content: `
        <p>You agree to indemnify and hold harmless Walnut Packaging Factory and its affiliates, officers, agents, and employees from any claim or demand, including reasonable attorneys' fees, made by any third party due to or arising out of:</p>
        <ul>
          <li>Your breach of these Terms</li>
          <li>Your violation of any law or rights of a third party</li>
          <li>Your use of designs that infringe on intellectual property rights</li>
          <li>Your misuse of our Service</li>
        </ul>
      `,
    },
    {
      title: "11. Privacy",
      icon: Shield,
      content: `
        <p>Your use of our Service is also governed by our Privacy Policy. Please review our <a href="/privacy" class="text-walnut-600 dark:text-walnut-400 hover:text-walnut-800 dark:hover:text-walnut-200 font-medium transition-colors underline">Privacy Policy</a>, which also governs the Service and informs users of our data collection practices.</p>
      `,
    },
    {
      title: "12. Modifications to Terms",
      icon: FileText,
      content: `
        <p>Walnut Packaging Factory reserves the right to modify these Terms at any time. We will notify users of any material changes by:</p>
        <ul>
          <li>Posting the new Terms on this page</li>
          <li>Updating the "Last Updated" date</li>
          <li>Sending email notifications to registered users</li>
        </ul>
        <p>Your continued use of the Service after such modifications constitutes your acceptance of the updated Terms.</p>
      `,
    },
    {
      title: "13. Governing Law",
      icon: Scale,
      content: `
        <p>These Terms shall be governed by and construed in accordance with the laws of the United States and the State of [Your State], without regard to its conflict of law provisions.</p>
        <p>Any legal action or proceeding arising under these Terms will be brought exclusively in the federal or state courts located in [Your County, State], and the parties hereby irrevocably consent to the personal jurisdiction and venue therein.</p>
      `,
    },
    {
      title: "14. Contact Information",
      icon: AlertCircle,
      content: `
        <p>If you have any questions about these Terms, please contact us:</p>
        <ul>
          <li><strong>Email:</strong> legal@walnutpackaging.com</li>
          <li><strong>Phone:</strong> +1 (555) 123-4567</li>
          <li><strong>Address:</strong> 123 Packaging Boulevard, Suite 100, Manufacturing City, ST 12345</li>
        </ul>
      `,
    },
  ];

  return (
    <div className="min-h-screen pt-32 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-walnut-700 dark:text-walnut-300 hover:text-walnut-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">{t("marketingPages.terms.back")}</span>
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-walnut-500 to-walnut-700 mb-6">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-walnut-900 dark:text-white mb-4">
            {t("marketingPages.terms.title")}
          </h1>
          <p className="text-lg text-walnut-700 dark:text-walnut-300">
            {t("marketingPages.terms.lastUpdated")}
          </p>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <GlassCard className="p-6 mb-8">
            <p className="text-walnut-700 dark:text-walnut-300 leading-relaxed">
              {t("marketingPages.terms.intro")}
            </p>
          </GlassCard>
        </motion.div>

        {/* Terms Sections */}
        <div className="space-y-6">
          {sections.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * (index + 3) }}
            >
              <GlassCard className="p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-walnut-100 dark:bg-walnut-800 flex items-center justify-center flex-shrink-0">
                    <section.icon className="w-5 h-5 text-walnut-700 dark:text-walnut-300" />
                  </div>
                  <h2 className="text-2xl font-bold text-walnut-900 dark:text-white">
                    {section.title}
                  </h2>
                </div>
                <div
                  className="prose prose-sm dark:prose-invert max-w-none
                    prose-headings:text-walnut-900 dark:prose-headings:text-white
                    prose-h4:text-lg prose-h4:font-semibold prose-h4:mb-2 prose-h4:mt-4
                    prose-p:text-walnut-700 dark:prose-p:text-walnut-300 prose-p:mb-3 prose-p:leading-relaxed
                    prose-li:text-walnut-700 dark:prose-li:text-walnut-300
                    prose-strong:text-walnut-900 dark:prose-strong:text-white
                    prose-ul:my-3 prose-ul:ml-6
                    prose-a:text-walnut-600 dark:prose-a:text-walnut-400"
                  dangerouslySetInnerHTML={{ __html: section.content }}
                />
              </GlassCard>
            </motion.div>
          ))}
        </div>

        {/* Footer CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12"
        >
          <GlassCard className="p-8 text-center">
            <h3 className="text-2xl font-bold text-walnut-900 dark:text-white mb-3">
              {t("marketingPages.terms.ctaTitle")}
            </h3>
            <p className="text-walnut-700 dark:text-walnut-300 mb-6">
              {t("marketingPages.terms.ctaSubtitle")}
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-[33px] bg-gradient-to-r from-walnut-600 to-walnut-700 dark:from-walnut-500 dark:to-walnut-600 text-white font-semibold hover:shadow-lg hover:shadow-walnut-500/30 transition-all duration-300 hover:scale-105"
            >
              {t("marketingPages.terms.ctaButton")}
            </Link>
          </GlassCard>
        </motion.div>
      </div>
    </div>
  );
};
